import { useState, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Button, Input, Heading, useToast, Text, Flex } from '@chakra-ui/react';
import apiClient from '../api/apiClient';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const toast = useToast();

  // ---------------- Password strength ----------------
  const getPasswordStrength = (password) => {
    let score = 0;
    if (!password) return { score, label: 'Too Short' };
    if (password.length >= 6) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[!@#$%^&*()_\-+=]/.test(password)) score += 1;

    let label = 'Weak';
    if (score >= 4) label = 'Strong';
    else if (score >= 2) label = 'Medium';
    return { score, label };
  };

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  const getStrengthColor = () => {
    switch (strength.label) {
      case 'Too Short': return 'gray.300';
      case 'Weak': return 'red.400';
      case 'Medium': return 'yellow.400';
      case 'Strong': return 'green.400';
      default: return 'gray.300';
    }
  };

  // ---------------- Handle submit ----------------
  const handleSubmit = async () => {
    if (newPassword.length < 6) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 6 characters long.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      await apiClient.post(`/auth/reset-password/${token}`, { newPassword });
      toast({
        title: 'Password reset successful',
        description: 'You can now log in with your new password.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setSuccess(true);
      setNewPassword('');
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.response?.data?.error || 'Failed to reset password.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW="md" mx="auto" mt={10} p={6} borderWidth={1} borderRadius="lg">
      <Heading size="md" mb={4}>Reset Password</Heading>

      {!success ? (
        <>
          <Input
            placeholder="Enter new password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            mb={2}
          />

          {/* Password strength bar */}
          {newPassword.length > 0 && (
            <Box mb={4}>
              <Text fontSize="sm" mb={1}>Strength: {strength.label}</Text>
              <Flex h="6px" borderRadius="md" overflow="hidden" bg="gray.200">
                <Box
                  w={`${(strength.score / 5) * 100}%`}
                  bg={getStrengthColor()}
                  transition="width 0.3s"
                />
              </Flex>
            </Box>
          )}

          <Button
            colorScheme="blue"
            width="100%"
            onClick={handleSubmit}
            isLoading={loading}
          >
            Reset Password
          </Button>
        </>
      ) : (
        <Text textAlign="center" mt={4}>
          Password reset successful!{' '}
          <RouterLink to="/login" style={{ color: '#3182CE', fontWeight: 'bold' }}>
            Go back to Login
          </RouterLink>
        </Text>
      )}
    </Box>
  );
}
